import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { day, get, money } from '../lib/api';
import { Badge, Card, ErrorNote, Loading, PageHeader } from '../ui';

/**
 * The audit, as it gets read out.
 *
 * Three lists, in this order and never any other: what is wrong, what could
 * not be checked, and what is genuinely fine. The middle one is the one that
 * keeps you honest in the room — an audit that quietly omits what it did not
 * look at is one you get caught by.
 */

type Finding = {
  id: string; rule: string; outcome: 'fail' | 'pass' | 'not_checked';
  severity: 'critical' | 'high' | 'medium' | 'low' | 'none';
  category: string; title: string; detail: string; fix: string | null;
  wasted: number | null; evidence: Record<string, unknown>[];
};

type Data = {
  audit: {
    id: string; account_id: string; account_name: string; customer_id: string;
    ran_at: string; period_start: string | null; period_end: string | null;
    currency: string; total_cost: number; wasted: number; summary: string; rule_version: string;
  };
  findings: Finding[];
};

const SEVERITY_LABEL: Record<Finding['severity'], string> = {
  critical: 'Fix this week', high: 'Serious', medium: 'Worth doing', low: 'Minor', none: '',
};

export default function Audit() {
  const { id } = useParams();
  const q = useQuery({ queryKey: ['audit', id], queryFn: () => get<Data>(`/v1/audits/${id}`) });

  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorNote error={q.error} />;
  const { audit, findings } = q.data;

  const fails = findings.filter((f) => f.outcome === 'fail');
  const notChecked = findings.filter((f) => f.outcome === 'not_checked');
  const passes = findings.filter((f) => f.outcome === 'pass');

  return (
    <>
      <Link to={`/accounts/${audit.account_id}`} className="mb-4 inline-flex items-center gap-1.5 text-sm text-t2 hover:text-t1">
        <ArrowLeft className="size-4" /> {audit.account_name}
      </Link>

      <PageHeader
        title={audit.account_name}
        subtitle={
          <>
            {audit.period_start ? <>{day(audit.period_start)} – {day(audit.period_end)}</> : 'Window unknown'}
            {' · '}audited {day(audit.ran_at)}
          </>
        }
      />

      {/* The sentence, before any table. It is what gets said out loud. */}
      <Card className="mb-6">
        <p className="text-lg leading-relaxed">{audit.summary}</p>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Spent" value={money(audit.total_cost, audit.currency)} />
          <Stat label="At risk" value={money(audit.wasted, audit.currency)} alert={Number(audit.wasted) > 0} />
          <Stat label="Problems" value={String(fails.length)} />
          <Stat label="Not checked" value={String(notChecked.length)} />
        </div>
        {Number(audit.wasted) > 0 && (
          <p className="mt-3 text-xs text-t3">
            &ldquo;At risk&rdquo; is an upper bound, and findings overlap on purpose — the same wasted search term appears
            in two of them because they are two different conversations. It is never more than what was actually spent.
          </p>
        )}
      </Card>

      {fails.length > 0 && (
        <Section title="What is wrong" subtitle="Worst first. Tracking before money, because a spend figure means nothing until you know what was being measured.">
          {fails.map((f) => <FindingCard key={f.id} f={f} currency={audit.currency} defaultOpen={f.severity === 'critical'} />)}
        </Section>
      )}

      {notChecked.length > 0 && (
        <Section title="What could not be checked" subtitle="Said out loud rather than left out. Each one names what would make it checkable.">
          {notChecked.map((f) => (
            <Card key={f.id} className="border-dashed">
              <div className="text-sm font-bold">{f.title}</div>
              <p className="mt-1 whitespace-pre-line text-sm text-t2">{f.detail}</p>
              {f.fix && <p className="mt-2 text-sm"><span className="font-medium">To check it: </span>{f.fix}</p>}
            </Card>
          ))}
        </Section>
      )}

      {passes.length > 0 && (
        <Section title="What is fine" subtitle="Ran, found nothing wrong. Worth saying: it is the half of the account that does not need touching.">
          <Card>
            <ul className="space-y-1.5">
              {passes.map((f) => (
                <li key={f.id} className="flex gap-2 text-sm">
                  <span className="text-ok">✓</span>
                  <span><span className="font-medium">{f.title}</span> <span className="text-t3">— {f.detail}</span></span>
                </li>
              ))}
            </ul>
          </Card>
        </Section>
      )}

      <p className="mt-8 text-xs text-t3">Rules version {audit.rule_version}</p>
    </>
  );
}

const Section = ({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) => (
  <section className="mb-8">
    <h2 className="text-sm font-bold">{title}</h2>
    <p className="mb-3 text-xs text-t3">{subtitle}</p>
    <div className="space-y-3">{children}</div>
  </section>
);

const Stat = ({ label, value, alert }: { label: string; value: string; alert?: boolean }) => (
  <div className="rounded-card border border-bd bg-bg3 px-3 py-2">
    <div className="text-xs text-t3">{label}</div>
    <div className={clsx('num text-lg font-bold tracking-tight', alert && 'text-err')}>{value}</div>
  </div>
);

function FindingCard({ f, currency, defaultOpen }: { f: Finding; currency: string; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const evidence = Array.isArray(f.evidence) ? f.evidence : [];

  return (
    <Card pad={false}>
      <div className="flex flex-wrap items-start gap-3 p-5">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={f.severity}>{SEVERITY_LABEL[f.severity]}</Badge>
            <span className="text-xs uppercase tracking-wide text-t3">{f.category}</span>
          </div>
          <h3 className="mt-1.5 text-base font-bold">{f.title}</h3>
          <p className="mt-1 whitespace-pre-line text-sm text-t2">{f.detail}</p>
          {f.fix && (
            <p className="mt-3 rounded-btn border border-bd bg-bg3 px-3 py-2 text-sm">
              <span className="font-medium">What to do: </span>{f.fix}
            </p>
          )}
        </div>
        {f.wasted != null && Number(f.wasted) > 0 && (
          <div className="text-right">
            <div className="num text-xl font-bold text-err">{money(f.wasted, currency)}</div>
            <div className="text-xs text-t3">at risk</div>
          </div>
        )}
      </div>

      {evidence.length > 0 && (
        <>
          <button
            onClick={() => setOpen((o) => !o)}
            className="flex w-full items-center gap-1.5 border-t border-bd px-5 py-2 text-left text-xs text-t2 hover:text-t1"
          >
            {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
            {open ? 'Hide' : 'Show'} the {evidence.length} rows this is based on
          </button>
          {open && (
            <div className="overflow-x-auto border-t border-bd">
              <table className="w-full text-sm">
                <thead className="bg-bg3 text-left text-xs font-medium text-t2">
                  <tr>{Object.keys(evidence[0]).map((k) => (
                    <th key={k} className="px-4 py-2">{k.replace(/_/g, ' ')}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {evidence.map((row, i) => (
                    <tr key={i} className="border-t border-bd">
                      {Object.keys(evidence[0]).map((k) => (
                        <td key={k} className="px-4 py-1.5 align-top">
                          {Array.isArray(row[k]) ? (row[k] as unknown[]).join(', ') : String(row[k] ?? '—')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
